import { cn } from '@/lib/utils';

/**
 * Drawer for the secondary stack with two presentation modes:
 *
 * - **inline**: non-overlapping side column that pushes the primary stack.
 *   Used on wide viewports.
 * - **overlay**: fixed-position panel with backdrop, slides in from right.
 *   Used on narrow viewports where the inline drawer won't fit.
 *
 * Both modes animate open/close and rely on the Stack's own controls
 * for dismissal — no extra close button needed.
 */
export function StackDrawer({
  open,
  mode = 'inline',
  onBackdropClick,
  children,
}: {
  open: boolean;
  mode?: 'inline' | 'overlay';
  onBackdropClick?: () => void;
  children: React.ReactNode;
}) {
  if (mode === 'overlay') {
    return (
      <>
        {/* Backdrop */}
        <div
          className={cn(
            'fixed inset-0 z-40 bg-black/50 transition-opacity duration-300',
            open ? 'opacity-100' : 'pointer-events-none opacity-0',
          )}
          onClick={onBackdropClick}
        />
        {/* Overlay panel */}
        <aside
          className={cn(
            'fixed inset-y-0 right-0 z-50 flex flex-col',
            'w-[min(calc(100vw-3rem),var(--container-lg))]',
            'bg-muted border-l shadow-[-8px_0_20px_rgba(0,0,0,0.25)]',
            'transition-transform duration-300 ease-in-out',
            open ? 'translate-x-0' : 'translate-x-full',
          )}
        >
          {children}
        </aside>
      </>
    );
  }

  // Inline mode
  return (
    <aside
      className={cn(
        'hidden shrink-0 flex-col overflow-hidden md:flex',
        'transition-[width,opacity] duration-300 ease-in-out',
        open
          ? 'w-[calc(var(--container-lg)+var(--spacing)*4)] border-l opacity-100'
          : 'w-0 opacity-0',
      )}
    >
      <div
        className={cn(
          'flex min-h-0 grow flex-col',
          'min-w-[calc(var(--container-lg)+var(--spacing)*4)]',
        )}
      >
        {children}
      </div>
    </aside>
  );
}
